import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/PageHero";
import FinalCTA from "@/components/FinalCTA";
import { FadeUp, Overline } from "@/components/Reveal";
import { SEO } from "@/lib/seo";
import { ShieldCheck, HeartHandshake, BookOpen } from "lucide-react";

const PRINCIPLES = [
  {
    icon: HeartHandshake,
    title: "Student-first, always",
    text: "Our guidance is built around the student's interests — not commissions, quotas or sales targets. We succeed when you make a decision you fully understand.",
  },
  {
    icon: ShieldCheck,
    title: "Transparency over promises",
    text: "We don't claim university partnerships, guaranteed scholarships, success rates or migration outcomes. Where information is unverified, we say so — and point you to the official source.",
  },
  {
    icon: BookOpen,
    title: "Accessible information",
    text: "Good guidance shouldn't depend on who you know. Vitaliah organises publicly available information about studying in Australia into one clear, free platform.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO title="About Vitaliah — Independent Australian Study Guidance" description="Vitaliah exists to make international education information clearer and easier to navigate. Student-first guidance, transparency and accessible information." />
      <Navbar />
      <PageHero
        overline="About Vitaliah"
        title="Clearer information. Better decisions."
        subtitle="Vitaliah exists to make international education information clearer and easier to navigate — starting with students exploring Australia."
      />
      <section className="py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-14">
            <div className="lg:col-span-5">
              <FadeUp><Overline>Our story</Overline></FadeUp>
              <FadeUp delay={0.1}>
                <h2 className="mt-6 font-serif text-4xl font-medium tracking-tight text-navy leading-snug">
                  Built for the students the system overlooks.
                </h2>
              </FadeUp>
            </div>
            <div className="lg:col-span-7 space-y-6 text-navy/70 leading-relaxed text-lg">
              <FadeUp delay={0.15}>
                <p>Researching study options abroad often means piecing together fragmented information — a university website here, an agent's pitch there, a forum thread from three years ago. Students and parents deserve better.</p>
              </FadeUp>
              <FadeUp delay={0.2}>
                <p>Vitaliah brings universities, scholarships, applications and student life together into one considered platform. We began with a focus on students from Bangladesh exploring Australian universities — and we're building the brand to serve students everywhere, across destinations.</p>
              </FadeUp>
              <FadeUp delay={0.25}>
                <p>We are an independent platform. We do not claim affiliations with universities, and we never present unverified figures as fact. Wherever our content is still being verified, it is clearly marked.</p>
              </FadeUp>
            </div>
          </div>

          <div className="mt-24 grid md:grid-cols-3 gap-6">
            {PRINCIPLES.map((p, i) => (
              <FadeUp key={p.title} delay={i * 0.1} className="h-full">
                <div data-testid={`principle-${i}`} className="h-full bg-white border border-border p-9 hover:border-gold hover:-translate-y-1 hover:shadow-lg transition-[transform,border-color,box-shadow] duration-500">
                  <p.icon className="h-7 w-7 text-gold" strokeWidth={1.5} />
                  <h3 className="mt-6 font-serif text-2xl font-medium text-navy">{p.title}</h3>
                  <p className="mt-4 text-sm text-navy/60 leading-relaxed">{p.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>
      <FinalCTA />
      <Footer />
    </div>
  );
}
